/* eslint-disable react/jsx-max-props-per-line */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import validate from 'validate.js';
import { FormHelperText, Grid, Typography } from '@mui/material';
import moment from 'moment';
import {
  ArquivoContainerV2,
  AuditTimelineV2,
  DatePickerV2,
  DefaultSelectV2,
  DialogDefaultV2,
  GerenciaSelectV2,
  InputV2,
  MalhaSelectV2,
  MultiSelectV2,
  PoloSelectV2,
  RichTextEditorV2,
  TabFormV2,
  TrueFalseSelectV2,
  WidgetV2,
  useAuditHistorico,
} from '../../../components/v2';
import BaseService from '../../../services/BaseService';
import FileService from '../../../services/FileService';
import { messageError, messageSuccess, messageWarning } from '../../../store/toast/actions';
import { hideLoader, showLoader } from '../../../store/loader/actions';
import {
  ACESSO_VEICULO_OPCOES,
  ACESSOS_OPCOES,
  EQUIPES_OPCOES,
  INSPECAO_MT_BT_ID,
  PRIORIDADE_AJUDA,
  PRIORIDADES,
  PRIORIDADES_NSMP_OPCOES,
  SERVICO_OUTROS_ID,
  emptyProcedimento,
} from './constants';
import {
  adminAlterado,
  historicoUrl,
  mensagemErro,
  obterAdmin,
  obterProcedimento,
  salvarProcedimento,
  valoresAdminDe,
  valoresDeDetalhe,
} from './api';
import AvisoRegra from './AvisoRegra';

const schema = {
  titulo: { presence: { allowEmpty: false, message: 'é obrigatório' } },
  // O `^` desliga o prefixo com o nome do atributo (`conteudoTexto` não é um rótulo da tela).
  conteudoTexto: { presence: { allowEmpty: false, message: '^O conteúdo é obrigatório' } },
};

/** O editor devolve `<p></p>` quando vazio: só conta como preenchido se sobrar texto fora das tags. */
const textoDoHtml = (html) => String(html || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/&nbsp;/g, ' ')
  .trim();

const Formulario = ({
  open,
  id,
  somenteLeitura,
  onClose,
  showLoader: show,
  hideLoader: hide,
  messageSuccess: toastOk,
  messageError: toastErro,
  messageWarning: toastAviso,
}) => {
  const [values, setValues] = useState(emptyProcedimento);
  const [errors, setErrors] = useState(null);
  const [saving, setSaving] = useState(false);
  const [servicos, setServicos] = useState([]);
  const [subtipos, setSubtipos] = useState([]);
  const [serverIds, setServerIds] = useState([]);
  // Área administrativa (só edição). `adminOriginal` é o que veio do GET, para saber se a seção mudou.
  const [admin, setAdmin] = useState(() => valoresAdminDe(null));
  const [adminOriginal, setAdminOriginal] = useState(null);
  const [adminDisponivel, setAdminDisponivel] = useState(false);
  const [tab, setTab] = useState(0);
  const copiaRef = useRef(false);

  const editando = Boolean(id && id !== 'novo' && !copiaRef.current);
  const comAdmin = editando && adminDisponivel;
  // Fica no formulário (e não na aba) para o `refetch()` depois de salvar: o cache do hook não se invalida sozinho.
  const historico = useAuditHistorico(id ? historicoUrl(id) : null, { enabled: open && comAdmin });

  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      try {
        const [servicoRes, subtipoRes] = await Promise.all([
          BaseService.get({ url: 'options/NSMP_SERVICOS_TIPO_SERVICO' }),
          BaseService.get({ url: 'options/NSIR_SUBTIPOS' }),
        ]);
        if (cancelado) return;
        setServicos(
          (servicoRes.data || [])
            .filter((opcao) => opcao.id !== SERVICO_OUTROS_ID)
            .map((opcao) => ({ value: opcao.id, label: opcao.value })),
        );
        setSubtipos(
          (subtipoRes.data || [])
            .filter((opcao) => String(opcao.parentId) === String(INSPECAO_MT_BT_ID))
            .map((opcao) => ({ value: opcao.id, label: opcao.value })),
        );
      } catch (error) {
        console.error(error);
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, []);

  useEffect(() => {
    if (!open) {
      copiaRef.current = false;
      return;
    }
    setTab(0);
    if (!id || id === 'novo') {
      if (!copiaRef.current) {
        setValues(emptyProcedimento());
        setServerIds([]);
        setErrors(null);
        setAdmin(valoresAdminDe(null));
        setAdminOriginal(null);
        setAdminDisponivel(false);
      }
      return;
    }

    let cancelado = false;
    const carregarAdmin = async () => {
      setAdminDisponivel(false);
      // Rotas administrativas exigem o módulo: quem só consulta nem pede.
      if (somenteLeitura) return;
      try {
        const dto = await obterAdmin(id);
        let anexos = dto?.anexos || [];
        if (anexos.length) {
          anexos = await FileService.downloadArquivos(anexos);
        }
        if (cancelado) return;
        const carregado = valoresAdminDe(dto, anexos);
        setAdmin(carregado);
        setAdminOriginal({
          observacao: carregado.observacao,
          solicitante: carregado.solicitante,
          vigenciaDesde: carregado.vigenciaDesde,
        });
        setAdminDisponivel(true);
      } catch (error) {
        // 403: sem o módulo, a seção e o histórico simplesmente não existem.
        if (!cancelado && error?.response?.status !== 403) {
          toastAviso({ message: mensagemErro(error, 'Não foi possível carregar a área administrativa.') });
        }
      }
    };
    const carregar = async () => {
      show();
      try {
        const detalhe = await obterProcedimento(id);
        let arquivos = detalhe?.anexos || [];
        if (arquivos.length) {
          arquivos = await FileService.downloadArquivos(arquivos);
        }
        if (cancelado) return;
        await carregarAdmin();
        if (cancelado) return;
        setServerIds((detalhe?.anexos || []).map((arquivo) => arquivo.id));
        setValues({
          ...valoresDeDetalhe(detalhe),
          arquivos,
          arquivoIdsRemovidos: [],
        });
        setErrors(null);
      } catch (error) {
        toastErro({ message: mensagemErro(error, 'Erro ao abrir o procedimento.') });
        onClose(false);
      } finally {
        hide();
      }
    };
    carregar();
    return () => {
      cancelado = true;
    };
  }, [open, id, somenteLeitura]);

  // Acesso é texto livre no backend: um valor fora do catálogo, vindo de um card salvo por fora, não pode sumir no próximo salvar.
  const acessoOpcoes = useMemo(() => {
    const extras = (values.acessos || [])
      .filter((acesso) => !ACESSOS_OPCOES.some((item) => item.value === acesso))
      .map((acesso) => ({ value: acesso, label: acesso }));
    return [...ACESSOS_OPCOES, ...extras];
  }, [values.acessos]);

  const selecionados = (catalogo, ids) =>
    catalogo.filter((item) => (ids || []).some((itemId) => String(itemId) === String(item.value)));

  const onArquivos = (lista) => {
    const atuais = lista || [];
    const removidos = serverIds.filter((arquivoId) => !atuais.some((arquivo) => arquivo.id === arquivoId));
    setValues((prev) => ({
      ...prev,
      arquivos: atuais,
      arquivoIdsRemovidos: removidos,
    }));
  };

  const duplicar = () => {
    copiaRef.current = true;
    setTab(0);
    setServerIds([]);
    setValues((prev) => ({
      ...prev,
      ativo: true,
      desativadoEm: null,
      desativadoPor: '',
      malhaIds: [],
      gerenciaIds: [],
      poloIds: [],
      arquivos: [],
      arquivoIdsRemovidos: [],
    }));
  };

  const onSave = async () => {
    if (somenteLeitura) return;
    const erros = validate({ ...values, conteudoTexto: textoDoHtml(values.conteudoHtml) }, schema);
    setErrors(erros || null);
    if (erros) {
      setTab(0);
      return;
    }
    const criar = copiaRef.current || !id || id === 'novo';
    setSaving(true);
    show();
    try {
      // Bloco `admin` só se a seção mudou: ausente = o BE não toca na parte administrativa.
      const enviarAdmin = !criar && comAdmin && adminAlterado(admin, adminOriginal);
      const salvo = await salvarProcedimento(criar ? null : id, enviarAdmin ? { ...values, admin } : values);
      if (salvo?.conteudoHtml != null) {
        setValues((prev) => ({ ...prev, conteudoHtml: salvo.conteudoHtml }));
      }
      toastOk({ message: 'Procedimento salvo.' });
      // O cadastro já existe: fechar mesmo com anexo falho, senão um novo "Salvar" criaria um duplicado.
      if (salvo?.uploadFalhou) {
        toastAviso({ message: 'O texto foi salvo, mas algum anexo não subiu. Reabra o procedimento e anexe de novo.' });
      }
      copiaRef.current = false;
      onClose(true);
    } catch (error) {
      toastErro({ message: mensagemErro(error, 'Erro ao salvar o procedimento.') });
    } finally {
      // O cache do histórico não se invalida sozinho; mesmo com falha no upload o PUT pode ter gravado.
      if (!criar && comAdmin) historico.refetch();
      setSaving(false);
      hide();
    }
  };

  const vigenciaTimestamp = admin.vigenciaDesde ? moment(admin.vigenciaDesde, 'YYYY-MM-DD').valueOf() : null;

  const secaoAdmin = (
    <Grid container spacing={1}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="body2" color="text.secondary">
          Descreva a última alteração (motivo, quem pediu, e-mail da CEMIG). O histórico guarda as anteriores.
        </Typography>
      </Grid>
      <Grid size={{ xs: 12, sm: 8 }}>
        <InputV2
          label="Solicitante"
          name="solicitante"
          placeholder="Quem pediu a mudança (ex.: Fulano, CEMIG – Gerência Centro)"
          inputProps={{ inputProps: { maxLength: 255 } }}
          value={admin.solicitante}
          onChange={(event) => setAdmin((prev) => ({ ...prev, solicitante: event.target.value }))}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <DatePickerV2
          label="Vigência desde"
          value={vigenciaTimestamp}
          onChange={(valor) => setAdmin((prev) => ({
            ...prev,
            vigenciaDesde: valor ? moment(valor).format('YYYY-MM-DD') : '',
          }))}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <InputV2
          label="Observação"
          name="observacao"
          multiline
          rows={3}
          placeholder="Motivo da mudança, texto puro"
          value={admin.observacao}
          onChange={(event) => setAdmin((prev) => ({ ...prev, observacao: event.target.value }))}
        />
      </Grid>
      {/* Só adiciona: anexo administrativo é evidência e não pode ser removido. */}
      <ArquivoContainerV2
        headerTitle="Anexos administrativos (e-mails, comprovantes)"
        arquivos={admin.arquivos}
        showDelete={false}
        onSave={(lista) => setAdmin((prev) => ({ ...prev, arquivos: lista || [] }))}
      />
    </Grid>
  );

  const dados = (
    <Grid container spacing={1}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <InputV2
          label="Título"
          name="titulo"
          isRequired
          disabled={somenteLeitura}
          error={!!(errors && errors.titulo)}
          helperText={errors && errors.titulo ? errors.titulo[0] : ''}
          value={values.titulo}
          onChange={(event) => setValues({ ...values, titulo: event.target.value })}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <DefaultSelectV2
          label="Importância"
          name="prioridade"
          showEmpty={false}
          disabled={somenteLeitura}
          options={PRIORIDADES}
          value={values.prioridade}
          useTooltip
          title={PRIORIDADE_AJUDA}
          placement="top"
          onSelect={(event) => setValues({ ...values, prioridade: event.target.value })}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 3 }}>
        <TrueFalseSelectV2
          label="Ativo"
          name="ativo"
          showEmpty={false}
          disabled={somenteLeitura}
          value={values.ativo === false ? 'false' : 'true'}
          onChange={(event) => setValues({ ...values, ativo: event.target.value !== 'false' })}
        />
      </Grid>
      {values.ativo === false && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputV2
            label="Desativado em"
            name="desativadoEm"
            disabled
            value={values.desativadoEm ? moment(values.desativadoEm).format('DD/MM/YYYY HH:mm') : ''}
            helperText={values.desativadoEm ? '' : 'Preenchido ao salvar'}
            showHelperText={!values.desativadoEm}
          />
        </Grid>
      )}
      {values.ativo === false && (
        <Grid size={{ xs: 12, sm: 6 }}>
          <InputV2
            label="Desativado por"
            name="desativadoPor"
            disabled
            value={values.desativadoPor || ''}
          />
        </Grid>
      )}
      <Grid size={{ xs: 12, sm: 6 }}>
        <InputV2 label="Inspeção" name="inspecao" value="Inspeção MT/BT" disabled />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <MultiSelectV2
          label="Subtipos"
          disabled={somenteLeitura}
          options={subtipos}
          defaultValue={selecionados(subtipos, values.subtipoIds)}
          onChange={(itens) => setValues({
            ...values,
            subtipoIds: itens ? itens.map((item) => item.value) : [],
          })}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <MalhaSelectV2
          multiSelect
          disabled={somenteLeitura}
          value={values.malhaIds}
          onSelect={(malhaIds) => setValues({ ...values, malhaIds: malhaIds || [] })}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <GerenciaSelectV2
          multiSelect
          disabled={somenteLeitura}
          value={values.gerenciaIds}
          onSelect={(gerenciaIds) => setValues({ ...values, gerenciaIds: gerenciaIds || [] })}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 4 }}>
        <PoloSelectV2
          isMultiple
          disabled={somenteLeitura}
          value={values.poloIds}
          onSelect={(poloIds) => setValues({ ...values, poloIds: poloIds || [] })}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        {/* Alinhado ao padding do cabeçalho do WidgetV2; o `&:last-child` zera o pb do CardContent. */}
        <WidgetV2
          title="Atributos da inspeção"
          subheader="O que o inspetor registra na NSMP. Em branco, vale para qualquer NSMP."
          collapse={false}
          initialExpanded
          sxContent={{
            px: { xs: 2, sm: 2.5 },
            py: 1.5,
            '&:last-child': { pb: 1.5 },
          }}
        >
          <Grid container spacing={1}>
            <Grid size={{ xs: 12 }}>
              <MultiSelectV2
                label="Serviços"
                disabled={somenteLeitura}
                options={servicos}
                defaultValue={selecionados(servicos, values.tipoServicoIds)}
                onChange={(itens) => setValues({
                  ...values,
                  tipoServicoIds: itens ? itens.map((item) => item.value) : [],
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <MultiSelectV2
                label="Equipe indicada"
                disabled={somenteLeitura}
                options={EQUIPES_OPCOES}
                defaultValue={selecionados(EQUIPES_OPCOES, values.equipes)}
                onChange={(itens) => setValues({
                  ...values,
                  equipes: itens ? itens.map((item) => item.value) : [],
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <MultiSelectV2
                label="Prioridade da NSMP"
                disabled={somenteLeitura}
                options={PRIORIDADES_NSMP_OPCOES}
                defaultValue={selecionados(PRIORIDADES_NSMP_OPCOES, values.prioridadesNsmp)}
                onChange={(itens) => setValues({
                  ...values,
                  prioridadesNsmp: itens ? itens.map((item) => item.value) : [],
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <MultiSelectV2
                label="Acesso"
                disabled={somenteLeitura}
                options={acessoOpcoes}
                defaultValue={selecionados(acessoOpcoes, values.acessos)}
                onChange={(itens) => setValues({
                  ...values,
                  acessos: itens ? itens.map((item) => item.value) : [],
                })}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <DefaultSelectV2
                label="Acesso de veículo"
                name="acessoVeiculo"
                showEmpty={false}
                disabled={somenteLeitura}
                options={ACESSO_VEICULO_OPCOES}
                value={values.acessoVeiculo}
                onSelect={(event) => setValues({ ...values, acessoVeiculo: event.target.value })}
              />
            </Grid>
          </Grid>
        </WidgetV2>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <AvisoRegra />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <RichTextEditorV2
          label="Conteúdo"
          variant="procedimento"
          isRequired
          disabled={somenteLeitura}
          rows={8}
          value={values.conteudoHtml}
          onChange={(conteudoHtml) => setValues({ ...values, conteudoHtml })}
        />
        {errors && errors.conteudoTexto && (
          <FormHelperText error>{errors.conteudoTexto[0]}</FormHelperText>
        )}
      </Grid>
      <ArquivoContainerV2
        headerTitle="Anexos"
        disabled={somenteLeitura}
        arquivos={values.arquivos}
        onSave={onArquivos}
      />
    </Grid>
  );

  const tabs = (
    <TabFormV2
      keepMounted
      currentTab={tab}
      onChangeTab={setTab}
      tabPanels={[
        { id: 'dados', label: 'Dados', element: dados },
        ...(comAdmin ? [
          { id: 'administrativo', label: 'Administrativo', element: secaoAdmin },
          {
            id: 'historico',
            label: 'Histórico',
            element: (
              <AuditTimelineV2
                items={historico.items}
                loading={historico.loading}
                loadError={historico.loadError}
                onRefresh={historico.refetch}
                emptyMessage="O histórico aparecerá aqui quando houver mudanças neste procedimento."
              />
            ),
          },
        ] : []),
      ]}
    />
  );

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={() => onClose(false)}
      handlerConfirm={onSave}
      title={editando ? 'Procedimento de digitação' : 'Novo procedimento de digitação'}
      confirmMessage="Salvar"
      confirmLoading={saving}
      readOnly={somenteLeitura}
      maxWidth="lg"
      fullWidth
      headerAction={[
        {
          title: 'Duplicar para outra região',
          onClick: duplicar,
          show: editando && !somenteLeitura,
          variant: 'outlined',
        },
      ]}
    >
      {tabs}
    </DialogDefaultV2>
  );
};

Formulario.propTypes = {
  open: PropTypes.bool.isRequired,
  id: PropTypes.string,
  somenteLeitura: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  showLoader: PropTypes.func.isRequired,
  hideLoader: PropTypes.func.isRequired,
  messageSuccess: PropTypes.func.isRequired,
  messageError: PropTypes.func.isRequired,
  messageWarning: PropTypes.func.isRequired,
};

Formulario.defaultProps = {
  id: null,
  somenteLeitura: false,
};

const mapDispatchToProps = (dispatch) => bindActionCreators({
  messageSuccess,
  messageError,
  messageWarning,
  showLoader,
  hideLoader,
}, dispatch);

export default connect(null, mapDispatchToProps)(Formulario);
